import React from "react";
import classnames from "classnames";
import kebabCase from "lodash.kebabcase";
import { ButtonColour, ButtonSize } from "@trussle/external-components";
import { IAccordionItemRaw } from "../../../types/IAccordionItem";
import { IContentfulBackgroundColour } from "../../../types/IContentfulBackgroundColour";
import { IContentfulRichTextResponse } from "../../../types/IContentfulRichText";
import { ICTA } from "../../../types/ICTA";
import { Accordion } from "../../Accordion";
import { addTracking, CallToActions } from "../../CallToActions";
import { ContentWithSidebar } from "../../ContentWithSidebar";
import { Heading } from "../../Heading";
import { RichText } from "../../RichText";
import { transformRichText } from "../../../helpers/richText";

import "./styles.scss";
import { PanelWrapper } from "../../PanelWrapper";

export interface IContentWithSidebarPanelProps {
  sections: IAccordionItemRaw[];
  title?: string;
  content?: IContentfulRichTextResponse;
  callToActions?: ICTA[];
  backgroundColour: IContentfulBackgroundColour;
  headingType?: string;
  headerAnchor?: string;
}

export const bgColourToContentColour = (bgColour: string) => {
  switch (bgColour) {
    case "white":
    case "grey":
    case "navy":
    case "lime-green":
    case "pink":
      return "sky-blue";
    default:
      return "white";
  }
};

export const ContentWithSidebarPanel = (props: IContentWithSidebarPanelProps) => {
  const transformedProps = {
    ...props,
    sections: props.sections.map((item) => ({
      ...item,
      content: transformRichText(item.content.raw),
    })),
    content: props.content
      ? transformRichText(props.content.raw)
      : null,
  };
  const {
    sections,
    title,
    content,
    callToActions,
    backgroundColour,
    headingType,
    headerAnchor,
  } = transformedProps;
  const contentWithSidebarPanel = classnames("contentWithSidebarPanel", {
    "contentWithSidebarPanel--background-navy": backgroundColour === "navy",
  });
  const secondaryButtonColour = backgroundColour === "grey" ? ButtonColour.TertiaryLight : ButtonColour.Secondary;
  return (
    <PanelWrapper backgroundColour={backgroundColour}>
      <div
        role="region"
        aria-label="Sidebar content"
        className={contentWithSidebarPanel}
        id={headerAnchor}
      >
        <div className="contentWithSidebarPanel__header">
          {title && (
          <Heading
            {...(title && {
              id: kebabCase(title.toLowerCase()),
            })}
            className="contentWithSidebarPanel__title"
            type={headingType}
          >
            {title}
          </Heading>
          )}
          {content && (
          <RichText
            contrastText={backgroundColour === "navy"}
            content={content}
          />
          )}
        </div>
        <div className="contentWithSidebarPanel__accordion">
          <Accordion
            accordion={sections}
            backgroundColour={bgColourToContentColour(backgroundColour)}
          />
        </div>
        <div className="contentWithSidebarPanel__contentWithSidebar">
          {sections && (
          <ContentWithSidebar
            data={sections}
            backgroundColour={bgColourToContentColour(backgroundColour)}
          />
          )}
        </div>
        {callToActions && (
        <div className="contentWithSidebarPanel__ctas">
          <CallToActions
            alignment="center"
            size={ButtonSize.Large}
            secondaryCtaColour={secondaryButtonColour}
            ctas={addTracking("Content with sidebar", ...callToActions)}
          />
        </div>
        )}
      </div>
    </PanelWrapper>
  );
};
